import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Recycle,
  Layers,
  Package,
  CircleCheck,
  MapPin,
  CreditCard,
  ShoppingCart,
} from "lucide-react";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { fetchProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";
import { getDepartmentBySlug } from "@/data/departments";

const FEATURED_SLUGS = [
  "carton-standard-renforce",
  "carton-a-livres",
  "carton-12-bouteilles",
  "carton-75-verres",
];

const DepartmentPage = () => {
  const { slug } = useParams();
  const dept = getDepartmentBySlug(slug);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchProducts()
      .then((res) => setProducts(res || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  useDocumentTitle(
    dept
      ? `Cartons de déménagement ${dept.nameWithArticle} (${dept.code}) — Livraison 24-48 h | cartonmove`
      : "Département introuvable — cartonmove",
    dept
      ? `Cartons solides, kits déménagement & emballage livrés ${dept.nameWithArticle} (${dept.code}). Service rapide, équipe dédiée, livraison à domicile en 24-48 h.`
      : undefined
  );

  if (!dept) return <Navigate to="/cartons-demenagement" replace />;

  const featured = FEATURED_SLUGS
    .map((s) => products.find((p) => p.slug === s))
    .filter(Boolean);

  return (
    <div data-testid="department-page" data-dept-slug={dept.slug}>
      {/* Breadcrumb back */}
      <div className="container-cdm pt-5">
        <Link
          to="/cartons-demenagement"
          className="inline-flex items-center gap-2 text-sm text-[#5b6b7a] hover:text-[#0f1d2b]"
          data-testid="dept-back-to-shop"
        >
          <ArrowRight className="h-4 w-4 rotate-180" /> Retour à la boutique
        </Link>
      </div>

      {/* ======= SECTION 1 ======= */}
      <section className="pt-10 pb-16 md:pt-14 md:pb-20 bg-white">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#EEF3F7] border border-[#cfdbe6] text-[11px] uppercase tracking-[0.18em] font-bold text-[#237cb5] mb-4">
              <MapPin className="h-3.5 w-3.5" strokeWidth={2.6} />
              Département {dept.code} · Île-de-France
            </div>
            <h1 className="font-display font-extrabold text-[28px] sm:text-[33px] md:text-[48px] text-[#0f1d2b] leading-tight">
              {dept.templateId === "yvelines"
                ? `Vente et livraison de cartons de déménagement dans le ${dept.code}`
                : dept.templateId === "essonne"
                ? "Cartons de déménagement : votre partenaire de confiance en Essonne"
                : dept.templateId === "hauts-de-seine"
                ? "Cartons de déménagement dans les Hauts-de-Seine (92)"
                : dept.templateId === "seine-saint-denis"
                ? "Solutions de cartons de déménagement en Seine-Saint-Denis (93)"
                : dept.templateId === "val-de-marne"
                ? "Cartons et fournitures de déménagement dans le Val-de-Marne (94)"
                : dept.templateId === "val-d-oise"
                ? "Pack & cartons : votre matériel de déménagement dans le 95"
                : `Cartons de déménagement ${dept.nameWithArticle}`}
            </h1>
            <p className="mt-3 text-[14px] text-[#5b6b7a]">{dept.intro}</p>
          </div>

          {/* 4 featured products */}
          {loading ? (
            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="aspect-square bg-zinc-100 animate-pulse rounded-2xl" />
              ))}
            </div>
          ) : (
            <div
              className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5"
              data-testid="dept-featured-products"
            >
              {featured.map((p, idx) => (
                <ProductCard key={p.id} product={p} index={idx} hideQty goToProduct />
              ))}
            </div>
          )}

          <p className="mt-10 text-[15px] md:text-base text-[#3a4856] leading-relaxed max-w-4xl">
            {dept.templateId === "seine-et-marne" ? (
              <>
                Préparer un déménagement demande du temps, de l&apos;organisation
                et surtout du matériel adapté. Pour vous accompagner à chaque
                étape, nous mettons à votre disposition une large sélection de
                cartons de déménagement, d&apos;accessoires de protection et de
                kits complets. Que vous déménagiez dans le 77 ou ailleurs en
                Île-de-France, profitez de solutions fiables et pratiques pour
                emballer vos biens en toute sérénité.
              </>
            ) : dept.templateId === "yvelines" ? (
              <>
                Préparez votre déménagement dans les meilleures conditions grâce
                à notre sélection de cartons et de fournitures d&apos;emballage
                dans les Yvelines. Cartons standards, cartons renforcés, papier
                bulle, adhésifs et kits complets : nous mettons à votre
                disposition tout le matériel nécessaire pour protéger
                efficacement vos biens et faciliter chaque étape de votre
                déménagement.
              </>
            ) : dept.templateId === "essonne" ? (
              <>
                Préparez votre déménagement en toute confiance grâce à notre
                gamme de cartons et de fournitures d&apos;emballage disponibles
                dans l&apos;Essonne. Cartons standards, modèles renforcés, kits
                complets et accessoires de protection : nous mettons à votre
                disposition des solutions adaptées pour emballer, protéger et
                transporter vos biens dans les meilleures conditions.
              </>
            ) : dept.templateId === "hauts-de-seine" ? (
              <>
                Organiser un déménagement dans les Hauts-de-Seine demande du
                matériel fiable et adapté aux contraintes de la région
                parisienne. Nous mettons à votre disposition une sélection
                complète de cartons et d&apos;accessoires d&apos;emballage
                conçus pour protéger efficacement vos biens et faciliter chaque
                étape de votre préparation. Une solution simple et pratique
                pour déménager dans le 92 en toute tranquillité.
              </>
            ) : dept.templateId === "seine-saint-denis" ? (
              <>
                Un déménagement en Seine-Saint-Denis demande une préparation
                soignée et du matériel adapté pour garantir la sécurité de vos
                biens. Nous vous accompagnons avec une gamme complète de
                cartons et d&apos;accessoires d&apos;emballage pensés pour
                simplifier votre organisation et protéger efficacement vos
                effets personnels tout au long de votre projet dans le 93.
              </>
            ) : dept.templateId === "val-de-marne" ? (
              <>
                Préparer un déménagement dans le Val-de-Marne demande des
                solutions d&apos;emballage efficaces et adaptées à chaque
                besoin. Nous vous proposons une gamme complète de cartons et
                d&apos;accessoires professionnels pour sécuriser vos biens et
                faciliter chaque étape de votre organisation dans le 94. Une
                solution pensée pour allier protection, praticité et sérénité
                tout au long de votre déménagement.
              </>
            ) : dept.templateId === "val-d-oise" ? (
              <>
                Que vous vous installiez à <strong className="text-[#0f1d2b]">Argenteuil</strong>,
                {" "}<strong className="text-[#0f1d2b]">Cergy</strong>,
                {" "}<strong className="text-[#0f1d2b]">Pontoise</strong> ou
                {" "}<strong className="text-[#0f1d2b]">Sarcelles</strong>, la
                réussite de votre départ commence par un matériel de qualité
                professionnelle. Finies les galères de cartons de récupération
                qui cèdent sous le poids de vos affaires : nous vous proposons
                des modèles robustes, renforcés et parfaitement adaptés à vos
                besoins. Commandez vos fournitures en quelques clics et
                bénéficiez d&apos;une livraison rapide directement chez vous
                partout dans le département pour emballer l&apos;esprit
                tranquille.
              </>
            ) : (
              <>
                Vous préparez votre déménagement {dept.nameWithArticle} et vous
                cherchez des cartons qui tiennent la route ? Vous êtes pile au
                bon endroit. Ici, on vous propose du matériel de qualité
                professionnelle, ultra-costaud et pensé pour vous faciliter la
                vie : du carton standard au carton livres renforcé, en passant
                par le papier bulle, le film étirable ou nos packs complets
                pour ne manquer de rien.
              </>
            )}
          </p>
          <Link
            to="/cartons-demenagement"
            className="mt-6 inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors [-webkit-tap-highlight-color:transparent]"
            data-testid="dept-cta-shop-top"
          >
            Voir tous les cartons
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </section>

      {dept.templateId === "seine-et-marne" ? (
        <SeineEtMarneSections dept={dept} />
      ) : dept.templateId === "yvelines" ? (
        <YvelinesSections dept={dept} />
      ) : dept.templateId === "essonne" ? (
        <EssonneSections dept={dept} />
      ) : dept.templateId === "hauts-de-seine" ? (
        <HautsDeSeineSections dept={dept} />
      ) : dept.templateId === "seine-saint-denis" ? (
        <SeineSaintDenisSections dept={dept} />
      ) : dept.templateId === "val-de-marne" ? (
        <ValDeMarneSections dept={dept} />
      ) : dept.templateId === "val-d-oise" ? (
        <ValDOiseSections dept={dept} />
      ) : (
        <ParisSections dept={dept} />
      )}
    </div>
  );
};

/* ============================================================
 * Template SEO — version "Paris" (par défaut pour les autres dépts)
 * ============================================================ */
const ParisSections = ({ dept }) => (
  <>
      <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="overline" style={{ color: "#237cb5" }}>
              Notre service
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Livraison de cartons directe chez vous par notre société partout
              en Île-de-France
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Gagnez du temps grâce à notre service de livraison de cartons{" "}
              {dept.nameWithArticle}. Nous acheminons directement à votre
              domicile, dans vos bureaux ou sur votre lieu de stockage
              l&apos;ensemble du matériel nécessaire à votre déménagement.
              Cartons, adhésifs, papier bulle et accessoires de protection sont
              livrés rapidement afin que vous puissiez préparer votre départ
              dans les meilleures conditions.
            </p>
          </div>

          <h3 className="mt-10 font-display font-extrabold text-lg md:text-xl text-[#0f1d2b]">
            Nos engagements pour votre livraison
          </h3>
          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                title: "Équipe dédiée",
                body: "Acheminement réalisé par nos propres transporteurs.",
                Icon: Truck,
              },
              {
                title: "Flexibilité horaire",
                body: "Organisation du rendez-vous selon votre emploi du temps.",
                Icon: ShieldCheck,
              },
              {
                title: "Service à l'adresse",
                body: "Matériel déposé directement à l'intérieur de votre domicile ou de vos locaux.",
                Icon: Package,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="bg-white rounded-2xl border border-[#cfdbe6] p-5"
              >
                <div className="flex items-center gap-2.5">
                  <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <h4 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                    {title}
                  </h4>
                </div>
                <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======= SECTION 3 — Cartons reconditionnés ======= */}
      <section className="py-14 md:py-20 bg-white">
        <div className="container-cdm">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7">
              <div className="overline" style={{ color: "#237cb5" }}>
                Économique &amp; écologique
              </div>
              <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
                Cartons reconditionnés {dept.nameWithArticle} : économisez
                jusqu&apos;à 40 %
              </h2>
              <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
                Optez pour nos cartons reconditionnés et profitez d&apos;une
                solution à la fois économique et écologique. Utilisés une seule
                fois puis soigneusement contrôlés par nos équipes, ils
                conservent toute leur solidité pour protéger efficacement vos
                biens. Une alternative fiable qui vous permet de réduire vos
                dépenses d&apos;emballage avec des prix jusqu&apos;à 40 %
                inférieurs à ceux des cartons neufs.
              </p>
              <Link
                to="/cartons-reconditionnes"
                className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
                data-testid="dept-cta-reconditionne"
              >
                Voir les cartons reconditionnés
                <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
              </Link>
            </div>
            <div className="lg:col-span-5 bg-[#EEF3F7] border border-[#cfdbe6] rounded-3xl p-6">
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center h-12 w-12 rounded-full bg-[#237cb5] text-white">
                  <Recycle className="h-6 w-6" strokeWidth={2.4} />
                </span>
                <div className="text-[40px] font-extrabold text-[#237cb5] leading-none">
                  -40 %
                </div>
              </div>
              <p className="mt-4 text-[14px] text-[#3a4856] leading-relaxed">
                Cartons contrôlés un par un, robustes et propres. La même
                résistance qu&apos;un carton neuf, à un prix bien plus doux.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ======= CTA Big Déménagement (au milieu) ======= */}
      <section className="py-12 bg-white">
        <div className="container-cdm">
          <BigDemenageurCTA testId={`dept-${dept.slug}-bigdem-cta-middle`} />
        </div>
      </section>

      {/* ======= SECTION 4 — Kits ======= */}
      <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="overline" style={{ color: "#237cb5" }}>
              Tout en un
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Kits de cartons de déménagement {dept.name}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Simplifiez la préparation de votre déménagement grâce à nos kits
              de cartons prêts à l&apos;emploi. Composés d&apos;un assortiment
              de cartons et de fournitures essentielles, ils vous permettent de
              disposer de tout le matériel nécessaire en une seule commande.
              Une solution pratique, économique et adaptée à différents volumes
              de déménagement.
            </p>
          </div>

          {/* Exemple Kit T1 */}
          <div className="mt-8 bg-white rounded-3xl border border-[#cfdbe6] p-6 md:p-8">
            <div className="flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center justify-center h-10 px-3 rounded-lg bg-[#237cb5] text-white text-[13px] font-extrabold tracking-wide">
                Exemple — Kit T1
              </span>
              <span className="text-[13.5px] text-[#5b6b7a] font-medium">
                Studio / 1 pièce — 15 à 25 m²
              </span>
            </div>
            <ul className="mt-5 space-y-3">
              {[
                "30 cartons standards : pour les fringues, le linge de lit, la déco et le matériel de cuisine.",
                "10 cartons livres : compacts et renforcés, parfaits pour vos bouquins, cours ou papiers importants sans vous bloquer le dos.",
                "1 rouleau de papier bulle (1 m × 10 m) : le petit format idéal pour emballer l'écran TV, les miroirs et la vaisselle fragile.",
                "3 rouleaux d'adhésif pro : de la qualité qui colle fort pour scotcher toutes vos boîtes sans mauvaise surprise.",
              ].map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 text-[14.5px] text-[#3a4856] leading-relaxed"
                >
                  <CircleCheck
                    className="h-5 w-5 text-[#237cb5] mt-0.5 shrink-0"
                    strokeWidth={2.4}
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/packs-cartons"
              className="mt-7 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
              data-testid="dept-cta-kits"
            >
              <Layers className="h-4 w-4" strokeWidth={2.6} />
              Découvrez tous nos kits (T2, T3, Maison)
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>
        </div>
      </section>

      {/* ======= SECTION 5 — Comment commander ======= */}
      <section className="py-14 md:py-20 bg-white">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="overline" style={{ color: "#237cb5" }}>
              Processus
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Comment lancer votre commande de cartons&nbsp;?
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              En trois étapes ultra simples, passez votre commande et recevez
              vos cartons chez vous {dept.nameWithArticle}.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                n: "01",
                title: "Choisissez vos cartons",
                body: "Sélectionnez vos packs ou vos cartons au détail dans la boutique.",
                Icon: ShoppingCart,
              },
              {
                n: "02",
                title: "Validez votre commande",
                body: "Validation en ligne 100 % sécurisée, paiement à la livraison.",
                Icon: CreditCard,
              },
              {
                n: "03",
                title: "Recevez vos cartons",
                body: `Notre équipe vous livre directement au pied de votre porte ${dept.nameWithArticle}.`,
                Icon: Truck,
              },
            ].map(({ n, title, body, Icon }) => (
              <div
                key={n}
                className="relative bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] p-5"
              >
                <div className="absolute top-4 right-4 text-[34px] font-extrabold text-[#237cb5]/15 leading-none">
                  {n}
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                    Étape {n.replace(/^0/, "")} — {title}
                  </h3>
                </div>
                <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======= SECTION 6 — Pourquoi nous faire confiance ======= */}
      <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="overline" style={{ color: "#237cb5" }}>
              Notre engagement
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Pourquoi nous faire confiance&nbsp;?
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Acheter des cartons, c&apos;est bien. Acheter les bons cartons à
              des pros du déménagement qui connaissent Paris et
              l&apos;Île-de-France sur le bout des doigts, c&apos;est mieux !
              On ne se contente pas de vous vendre des fournitures : on
              s&apos;assure que votre logistique soit un sans-faute du début à
              la fin.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                title: "Qualité Pro",
                body: "Des cartons robustes qui ne prennent pas l'humidité et ne craquent pas au fond du camion.",
                Icon: ShieldCheck,
              },
              {
                title: "Éco-responsable & éco-malin",
                body: "Nos cartons reconditionnés de premier choix à -40 % pour faire du bien à la planète et au portefeuille.",
                Icon: Recycle,
              },
              {
                title: "Livraison 100 % interne",
                body: "Zéro sous-traitant, zéro colis perdu. C'est notre propre flotte de véhicules qui vous livre.",
                Icon: Truck,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="bg-white rounded-2xl border border-[#cfdbe6] p-5"
              >
                <div className="flex items-center gap-2.5">
                  <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#FF7A00] text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                    {title}
                  </h3>
                </div>
                <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link
              to="/cartons-demenagement"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
              data-testid="dept-cta-shop"
            >
              Voir tous les cartons
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
            <Link
              to="/packs-cartons"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
              data-testid="dept-cta-kits-bottom"
            >
              <Layers className="h-4 w-4" strokeWidth={2.6} />
              Choisir un kit
            </Link>
          </div>
        </div>
      </section>
  </>
);

/* ============================================================
 * Template SEO — version "Seine-et-Marne" (77) — contenu unique
 * ============================================================ */
const SeineEtMarneSections = ({ dept }) => (
  <>
    {/* ======= SECTION 2 — Livraison (4 engagements) ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Notre service
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Livraison de cartons de déménagement {dept.nameWithArticle} ({dept.code})
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Recevez directement chez vous tout le matériel nécessaire à la
            préparation de votre déménagement. Nous assurons la livraison de
            cartons, adhésifs, protections et accessoires dans l&apos;ensemble
            de la {dept.name} ({dept.code}), afin de vous faire gagner du temps
            et de vous permettre d&apos;emballer vos biens dans les meilleures
            conditions. Rapide et pratique, notre service vous évite tout
            déplacement inutile.
          </p>
        </div>

        <h3 className="mt-10 font-display font-extrabold text-lg md:text-xl text-[#0f1d2b]">
          Nos engagements de livraison
        </h3>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              title: "Livraison rapide",
              body: `Livraison rapide dans toute la ${dept.name} (${dept.code}).`,
              Icon: Truck,
            },
            {
              title: "Délais respectés",
              body: "Respect des délais annoncés pour une organisation sans stress.",
              Icon: ShieldCheck,
            },
            {
              title: "Matériel contrôlé",
              body: "Matériel soigneusement préparé et contrôlé avant expédition.",
              Icon: Package,
            },
            {
              title: "Service client réactif",
              body: "Service client réactif pour vous accompagner à chaque étape de votre commande.",
              Icon: CircleCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h4 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                  {title}
                </h4>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — À quel moment commander ? ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Anticipation
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Préparer son déménagement : à quel moment commander vos cartons&nbsp;?
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Nous recommandons de commander vos cartons de déménagement au moins
            <strong className="text-[#0f1d2b]"> une à deux semaines </strong>
            avant la date prévue de votre départ. Ce délai vous permet de
            recevoir votre matériel, de commencer l&apos;emballage sereinement
            et d&apos;éviter toute précipitation de dernière minute.
          </p>
          <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            En période de forte activité, notamment durant les vacances
            scolaires et l&apos;été, il est conseillé d&apos;anticiper davantage
            afin de garantir la disponibilité des produits souhaités.
          </p>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-white">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`dept-${dept.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Comment obtenir vos cartons ? ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Commande en 4 étapes
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Comment obtenir vos cartons de déménagement&nbsp;?
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Commander vos cartons de déménagement est simple et rapide.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              n: "01",
              title: "Sélectionnez",
              body: "Choisissez les cartons, kits ou accessoires dont vous avez besoin.",
              Icon: ShoppingCart,
            },
            {
              n: "02",
              title: "Ajoutez au panier",
              body: "Ajoutez les produits souhaités à votre panier en un clic.",
              Icon: Package,
            },
            {
              n: "03",
              title: "Validez",
              body: "Validez votre commande en quelques clics, en toute sécurité.",
              Icon: CreditCard,
            },
            {
              n: "04",
              title: "Recevez",
              body: "Recevez votre matériel directement à l'adresse de votre choix.",
              Icon: Truck,
            },
          ].map(({ n, title, body, Icon }) => (
            <div
              key={n}
              className="relative bg-white rounded-2xl border border-[#cfdbe6] p-5"
            >
              <div className="absolute top-4 right-4 text-[34px] font-extrabold text-[#237cb5]/15 leading-none">
                {n}
              </div>
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                  Étape {n.replace(/^0/, "")} — {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <p className="mt-8 text-[15px] md:text-base text-[#3a4856] leading-relaxed max-w-3xl">
          Une fois votre commande confirmée, nous préparons et expédions vos
          articles dans les meilleurs délais afin que vous puissiez organiser
          votre déménagement en toute sérénité.
        </p>

        <div className="mt-7 flex flex-wrap items-center gap-3">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="dept-cta-shop"
          >
            Voir tous les cartons
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/packs-cartons"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="dept-cta-kits-bottom"
          >
            <Layers className="h-4 w-4" strokeWidth={2.6} />
            Choisir un kit
          </Link>
        </div>
      </div>
    </section>
  </>
);

/* ============================================================
 * Template SEO — version "Yvelines" (78) — contenu unique
 * ============================================================ */
const YvelinesSections = ({ dept }) => (
  <>
    {/* ======= SECTION 2 — Livraison (4 engagements Yvelines) ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Notre service
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Livraison de cartons de déménagement {dept.nameWithArticle} ({dept.code})
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Profitez de notre service de livraison de cartons dans
            l&apos;ensemble des Yvelines. Que vous soyez à Versailles,
            Saint-Germain-en-Laye, Poissy, Mantes-la-Jolie, Rambouillet ou dans
            une autre commune du département, nous vous livrons directement le
            matériel nécessaire à la préparation de votre déménagement. Une
            solution pratique pour recevoir rapidement vos cartons, protections
            et accessoires sans avoir à vous déplacer.
          </p>
        </div>

        <h3 className="mt-10 font-display font-extrabold text-lg md:text-xl text-[#0f1d2b]">
          Nos engagements
        </h3>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              title: "Livraison rapide",
              body: "Livraison rapide partout dans les Yvelines (78).",
              Icon: Truck,
            },
            {
              title: "Qualité professionnelle",
              body: "Produits de qualité professionnelle pour protéger efficacement vos biens.",
              Icon: ShieldCheck,
            },
            {
              title: "Tarifs transparents",
              body: "Tarifs transparents sans frais cachés ni mauvaises surprises.",
              Icon: CircleCheck,
            },
            {
              title: "Service client à l'écoute",
              body: "Service client à votre écoute pour vous conseiller et vous accompagner dans vos achats.",
              Icon: Package,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h4 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                  {title}
                </h4>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Cartons reconditionnés Yvelines ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Économique &amp; écologique
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Cartons reconditionnés {dept.nameWithArticle} ({dept.code}) :
              jusqu&apos;à 40 % d&apos;économies
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Faites le choix d&apos;un emballage malin avec nos cartons
              reconditionnés disponibles dans les Yvelines. Après une unique
              utilisation, chaque carton est rigoureusement vérifié et
              sélectionné afin de garantir un excellent niveau de résistance.
              Cette solution vous permet de limiter votre budget tout en
              adoptant une démarche plus respectueuse de l&apos;environnement,
              avec des tarifs pouvant être jusqu&apos;à 40 % moins élevés que
              ceux des cartons neufs.
            </p>
            <Link
              to="/cartons-reconditionnes"
              className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
              data-testid="dept-cta-reconditionne"
            >
              Voir les cartons reconditionnés
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>
          <div className="lg:col-span-5 bg-[#EEF3F7] border border-[#cfdbe6] rounded-3xl p-6">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-12 w-12 rounded-full bg-[#237cb5] text-white">
                <Recycle className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <div className="text-[40px] font-extrabold text-[#237cb5] leading-none">
                -40 %
              </div>
            </div>
            <p className="mt-4 text-[14px] text-[#3a4856] leading-relaxed">
              Cartons contrôlés un par un, robustes et propres. La même
              résistance qu&apos;un carton neuf, à un prix bien plus doux.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-white">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`dept-${dept.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Packs complets + Kit T3 ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Pack complet
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Simplifiez votre déménagement avec nos packs complets
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Vous préparez le déménagement au 78&nbsp;? Nos packs de cartons ont
            été conçus pour vous fournir l&apos;essentiel du matériel
            nécessaire à l&apos;emballage de vos biens. Composés d&apos;un
            assortiment de cartons et d&apos;accessoires adaptés aux
            différentes tailles de logements, ils vous permettent
            d&apos;organiser votre déménagement plus facilement tout en
            maîtrisant votre budget. Une solution pratique, complète et
            disponible à la livraison partout dans les Yvelines.
          </p>
        </div>

        {/* Exemple Kit T3 */}
        <div className="mt-8 bg-white rounded-3xl border border-[#cfdbe6] p-6 md:p-8">
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center justify-center h-10 px-3 rounded-lg bg-[#237cb5] text-white text-[13px] font-extrabold tracking-wide">
              Exemple — Kit T3
            </span>
            <span className="text-[13.5px] text-[#5b6b7a] font-medium">
              Appartement 3 pièces — 55 à 75 m²
            </span>
          </div>
          <ul className="mt-5 space-y-3">
            {[
              "60 cartons standards : le gros volume indispensable pour emballer le quotidien de toute la famille.",
              "30 cartons livres : costauds et parfaits pour vos collections de BD, romans, archives ou objets lourds.",
              "1 grand rouleau de papier bulle (0,5 m × 100 m) : 100 mètres de long ! La sécurité ultime et ultra-généreuse pour toute votre vaisselle et vos bibelots.",
              "6 rouleaux d'adhésif pro : le stock parfait pour scotcher à la chaîne comme un vrai pro de la manutention.",
            ].map((item, i) => (
              <li
                key={i}
                className="flex items-start gap-3 text-[14.5px] text-[#3a4856] leading-relaxed"
              >
                <CircleCheck
                  className="h-5 w-5 text-[#237cb5] mt-0.5 shrink-0"
                  strokeWidth={2.4}
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <Link
            to="/packs-cartons"
            className="mt-7 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="dept-cta-kits"
          >
            <Layers className="h-4 w-4" strokeWidth={2.6} />
            Découvrez nos autres kits (T1, T2 et le kit Maison)
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — Nos villes phares 78 ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Nos villes phares
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Les villes des Yvelines où nous livrons le plus
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Chaque semaine, nous accompagnons des dizaines de particuliers et
            d&apos;entreprises dans leur déménagement aux quatre coins du 78.
            Voici nos huit villes phares avec des pages dédiées proposant
            conseils, formats et tarifs adaptés à chaque commune.
          </p>
        </div>

        <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2.5 text-[15px] md:text-base">
          {[
            { name: "Versailles", slug: "versailles" },
            { name: "Trappes", slug: "trappes" },
            { name: "Plaisir", slug: "plaisir" },
            { name: "Poissy", slug: "poissy" },
            { name: "Maisons-Laffitte", slug: "maisons-laffitte" },
            { name: "Chatou", slug: "chatou" },
            { name: "Houilles", slug: "houilles" },
            { name: "Sartrouville", slug: "sartrouville" },
          ].map(({ name, slug }) => (
            <li key={slug}>
              <Link
                to={`/carton-demenagement-${slug}`}
                className="inline-flex items-center font-semibold text-[#237cb5] underline underline-offset-4 decoration-[#237cb5]/40 hover:decoration-[#237cb5] hover:text-[#0f1d2b] transition-colors"
                data-testid={`dept-78-city-${slug}`}
              >
                Carton déménagement {name}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  </>
);

/* ============================================================
 * Template SEO — version "Essonne" (91) — contenu unique
 * ============================================================ */
const EssonneSections = ({ dept }) => (
  <>
    {/* ======= SECTION 2 — Livraison + 4 engagements Essonne ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Notre service
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Livraison de cartons de déménagement {dept.nameWithArticle} ({dept.code})
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Recevez facilement tout le nécessaire pour votre déménagement
            directement {dept.nameWithArticle}. Nous livrons cartons,
            protections et accessoires partout dans le département afin de vous
            permettre de préparer votre installation sans contrainte et sans
            déplacement.
          </p>
        </div>

        <h3 className="mt-10 font-display font-extrabold text-lg md:text-xl text-[#0f1d2b]">
          Nos engagements
        </h3>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              title: "Expédition rapide",
              body: "Expédition rapide sur l'ensemble du territoire de l'Essonne (91).",
              Icon: Truck,
            },
            {
              title: "Produits fiables",
              body: "Produits fiables et résistants pour assurer la protection de vos biens.",
              Icon: ShieldCheck,
            },
            {
              title: "Préparation soignée",
              body: "Préparation soignée des commandes avant envoi.",
              Icon: Package,
            },
            {
              title: "Accompagnement client",
              body: "Accompagnement client attentif pour répondre à vos demandes.",
              Icon: CircleCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h4 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                  {title}
                </h4>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Matériel livré chez vous Essonne ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison locale
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Votre matériel de déménagement livré chez vous {dept.nameWithArticle} ({dept.code})
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Vous habitez à Évry, Massy, Corbeil-Essonnes ou Savigny-sur-Orge ?
            Ne perdez plus votre temps à courir les magasins à la recherche de
            fournitures. Nous vous simplifions la vie. Profitez de notre
            service de livraison rapide directement à votre domicile ou sur
            votre lieu de travail partout dans le 91. Nous acheminons vos
            cartons, rouleaux de bulles et kits de protection avec notre propre
            flotte de véhicules, l&apos;assurance d&apos;un service ponctuel et
            de cartons livrés en parfait état.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              title: "Zéro stress",
              body: "Plus besoin de surcharger votre voiture avec des cartons volumineux.",
              Icon: ShieldCheck,
            },
            {
              title: "Rapidité",
              body: "Une livraison programmée selon vos disponibilités pour ne pas retarder vos préparatifs.",
              Icon: Truck,
            },
            {
              title: "Proximité",
              body: "Un service local de confiance pour tous les habitants de l'Essonne.",
              Icon: MapPin,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] p-5"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#FF7A00] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 bg-[#0f1d2b] text-white rounded-3xl p-6 md:p-7 flex flex-col md:flex-row md:items-center gap-5">
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-white/60">
              On se charge du reste
            </div>
            <p className="mt-2 text-[17px] md:text-[18px] font-extrabold leading-snug">
              Commandez aujourd&apos;hui, emballez demain&nbsp;!
            </p>
            <p className="mt-1.5 text-[13.5px] text-white/80">
              Sélectionnez vos produits, validez votre panier et programmez
              votre livraison {dept.nameWithArticle} en toute simplicité.
            </p>
          </div>
          <Link
            to="/cartons-demenagement"
            className="shrink-0 inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#FF7A00] hover:bg-[#E66E00] text-white font-bold text-[14px] transition-colors"
            data-testid="dept-essonne-cta-shop"
          >
            Commander mes cartons
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-white">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`dept-${dept.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — À chaque objet son carton ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Le bon carton au bon endroit
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            À chaque objet son carton : faites le bon choix pour votre
            déménagement dans le 91&nbsp;!
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour éviter les mauvaises surprises sur les routes de l&apos;Essonne,
            de Corbeil-Essonnes à Savigny-sur-Orge ou de Sainte-Geneviève-des-Bois
            à Viry-Châtillon, oubliez le carton unique. Un emballage réussi
            repose sur du matériel adapté au poids et à la fragilité de vos
            affaires.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              slug: "carton-standard-renforce",
              title: "Le Carton Standard Renforcé",
              body:
                "Votre meilleur allié pour le quotidien. Idéal pour le linge de maison, la vaisselle bien protégée ou la déco de votre logement.",
              Icon: Package,
            },
            {
              slug: "carton-a-livres",
              title: "Le Carton Livres",
              body:
                "Plus petit mais ultra-costaud, il est conçu spécifiquement pour supporter les charges lourdes (livres, dossiers, documents administratifs) sans risquer de craquer pendant la manutention.",
              Icon: ShieldCheck,
            },
            {
              slug: "carton-penderie-grand",
              title: "Le Carton Penderie",
              body:
                "Un vrai gain de temps. Il vous permet de suspendre directement vos vêtements sur cintres pour les déménager sans les froisser.",
              Icon: Layers,
            },
            {
              slug: "carton-12-bouteilles",
              title: "Le Carton Bouteilles",
              body:
                "Équipé de croisillons protecteurs, il maintient et isole vos bouteilles pour un transport 100 % sécurisé, sans aucun risque de casse dans le camion.",
              Icon: CircleCheck,
            },
          ].map(({ slug, title, body, Icon }) => (
            <Link
              key={slug}
              to={`/produit/${slug}`}
              className="group bg-white rounded-2xl border border-[#cfdbe6] p-5 hover:border-[#237cb5] hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-16px_rgba(35,124,181,0.35)] transition-all duration-300 block"
              data-testid={`dept-essonne-carton-${slug}`}
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white shrink-0">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[13.5px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
              <div className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-bold text-[#237cb5] opacity-0 group-hover:opacity-100 transition-opacity">
                Voir ce carton <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.6} />
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-8 bg-white border-l-4 border-[#FF7A00] rounded-r-2xl p-5 max-w-4xl">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF7A00]">
            Le conseil pro
          </div>
          <p className="mt-2 text-[14.5px] text-[#3a4856] leading-relaxed">
            Pour un chargement efficace et des cartons faciles à empiler, mixez
            les formats. Adapter la taille du carton à son contenu, c&apos;est
            l&apos;assurance de protéger vos biens les plus précieux lors de
            votre déménagement {dept.nameWithArticle}.
          </p>
        </div>
      </div>
    </section>
  </>
);

/* ============================================================
 * Template SEO — version "Hauts-de-Seine" (92) — contenu unique
 * ============================================================ */
const HautsDeSeineSections = ({ dept }) => (
  <>
    {/* ======= SECTION 2 — Livraison + 4 engagements 92 ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Notre service
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Livraison de cartons de déménagement {dept.nameWithArticle} ({dept.code})
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Nous assurons la livraison de vos cartons et fournitures de
            déménagement directement {dept.nameWithArticle}. Que vous soyez à
            Boulogne-Billancourt, Nanterre, Courbevoie, Neuilly-sur-Seine ou
            dans une autre commune du département, vous recevez rapidement tout
            le matériel nécessaire pour préparer votre déménagement sans
            contrainte.
          </p>
        </div>

        <h3 className="mt-10 font-display font-extrabold text-lg md:text-xl text-[#0f1d2b]">
          Nos engagements
        </h3>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              title: "Livraison rapide 92",
              body: "Livraison rapide dans l'ensemble du département des Hauts-de-Seine (92).",
              Icon: Truck,
            },
            {
              title: "Matériel robuste",
              body: "Matériel robuste et adapté pour sécuriser vos biens pendant l'emballage et le transport.",
              Icon: ShieldCheck,
            },
            {
              title: "Préparation minutieuse",
              body: "Préparation minutieuse des commandes avant expédition.",
              Icon: Package,
            },
            {
              title: "Service client disponible",
              body: "Service client disponible pour vous accompagner et vous conseiller.",
              Icon: CircleCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h4 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                  {title}
                </h4>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Cartons reconditionnés 92 ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Économique &amp; responsable
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Cartons reconditionnés {dept.nameWithArticle} ({dept.code}) :
              une solution économique et responsable
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Optez pour une alternative raffinée avec nos cartons
              reconditionnés disponibles {dept.nameWithArticle}. Après une
              première utilisation unique, chaque carton est minutieusement
              trié, contrôlé et remis en circulation afin de garantir un niveau
              de qualité et de résistance optimal. Cette solution vous permet
              de maîtriser votre budget d&apos;emballage tout en profitant de
              tarifs réduits pouvant atteindre <strong className="text-[#0f1d2b]">-40 %</strong>
              {" "}par rapport au neuf, sans compromis sur la fiabilité.
            </p>
            <Link
              to="/cartons-reconditionnes"
              className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
              data-testid="dept-cta-reconditionne"
            >
              Voir les cartons reconditionnés
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>
          <div className="lg:col-span-5 bg-[#EEF3F7] border border-[#cfdbe6] rounded-3xl p-6">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-12 w-12 rounded-full bg-[#237cb5] text-white">
                <Recycle className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <div className="text-[40px] font-extrabold text-[#237cb5] leading-none">
                -40 %
              </div>
            </div>
            <p className="mt-4 text-[14px] text-[#3a4856] leading-relaxed">
              Tri rigoureux et contrôle qualité de chaque carton avant remise
              en circulation. Une démarche écologique sans compromis sur la
              résistance.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-white">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`dept-${dept.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Préparer ses cartons pour garde-meuble ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Guide pratique
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Comment bien préparer ses cartons et meubles destinés au stockage
            dans un garde-meuble
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Un stockage en garde-meuble exige une préparation minutieuse afin
            de garantir la bonne conservation de vos biens dans le temps.
            L&apos;utilisation de protections adaptées et de matériaux de
            qualité est essentielle pour éviter toute dégradation.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            "Utilisez des cartons solides et adaptés au poids de vos objets pour assurer leur stabilité dans le temps.",
            "Protégez vos objets fragiles avec du papier bulle ou des matériaux de calage afin de limiter les chocs.",
            "Fermez soigneusement chaque carton avec un adhésif résistant pour garantir une bonne tenue en stockage.",
            "Étiquetez clairement vos cartons pour faciliter l'identification lors de la récupération.",
            "Protégez vos meubles avec des couvertures de déménagement ou des housses adaptées pour éviter rayures et poussière.",
            "Utilisez du papier kraft pour envelopper les surfaces sensibles comme le bois, le verre ou les éléments décoratifs.",
            "Surélevez si possible les meubles et cartons afin de les isoler du sol et de l'humidité.",
            "Évitez de surcharger les contenants pour préserver leur intégrité pendant toute la durée du stockage.",
          ].map((tip, i) => (
            <div
              key={i}
              className="flex items-start gap-3 bg-white rounded-xl border border-[#cfdbe6] px-4 py-3.5"
            >
              <span className="flex items-center justify-center h-7 w-7 rounded-full bg-[#237cb5] text-white text-[12px] font-extrabold shrink-0 mt-0.5">
                {i + 1}
              </span>
              <p className="text-[14px] text-[#3a4856] leading-relaxed">{tip}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Link
            to="/emballage-protection"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="dept-92-cta-emballage"
          >
            <ShieldCheck className="h-4 w-4" strokeWidth={2.6} />
            Voir nos protections et emballages
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="dept-92-cta-shop"
          >
            Tous les cartons
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — Villes les plus actives dans le 92 ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Nos villes phares
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Les villes des Hauts-de-Seine où nous livrons le plus
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Chaque semaine, nous accompagnons des dizaines de particuliers et
            d&apos;entreprises dans leur déménagement aux quatre coins du 92.
            Voici nos cinq villes phares avec des pages dédiées proposant
            conseils, formats et tarifs adaptés à chaque commune.
          </p>
        </div>

        <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2.5 text-[15px] md:text-base">
          {[
            { name: "Neuilly-sur-Seine", slug: "neuilly-sur-seine" },
            { name: "Boulogne-Billancourt", slug: "boulogne-billancourt" },
            { name: "Nanterre", slug: "nanterre" },
            { name: "Montrouge", slug: "montrouge" },
            { name: "Sèvres", slug: "sevres" },
          ].map(({ name, slug }) => (
            <li key={slug}>
              <Link
                to={`/carton-demenagement/${slug}`}
                className="inline-flex items-center font-semibold text-[#237cb5] underline underline-offset-4 decoration-[#237cb5]/40 hover:decoration-[#237cb5] hover:text-[#0f1d2b] transition-colors"
                data-testid={`dept-92-city-${slug}`}
              >
                Carton déménagement {name}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  </>
);

/* ============================================================
 * Template SEO — version "Seine-Saint-Denis" (93) — contenu unique
 * ============================================================ */
const SeineSaintDenisSections = ({ dept }) => (
  <>
    {/* ======= SECTION 2 — Livraison + 4 engagements 93 ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Notre service
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Livraison de cartons de déménagement et fournitures d&apos;emballage
            {" "}{dept.nameWithArticle} ({dept.code})
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Nous assurons la livraison de vos cartons de déménagement ainsi
            que de l&apos;ensemble des fournitures de protection et
            d&apos;emballage directement {dept.nameWithArticle}. Que vous soyez
            à Saint-Denis, Montreuil, Aulnay-sous-Bois, Aubervilliers ou dans
            une autre commune du département, vous recevez rapidement tout le
            matériel nécessaire pour préparer votre déménagement dans les
            meilleures conditions.
          </p>
        </div>

        <h3 className="mt-10 font-display font-extrabold text-lg md:text-xl text-[#0f1d2b]">
          Nos engagements
        </h3>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              title: "Livraison rapide 93",
              body: "Livraison rapide dans tout le département de la Seine-Saint-Denis (93).",
              Icon: Truck,
            },
            {
              title: "Matériel complet",
              body: "Cartons, adhésifs, films de protection et accessoires d'emballage.",
              Icon: Package,
            },
            {
              title: "Préparation soignée",
              body: "Préparation soignée des commandes pour garantir la qualité à réception.",
              Icon: ShieldCheck,
            },
            {
              title: "Service réactif",
              body: "Service réactif pour répondre à vos besoins et vous accompagner dans vos choix.",
              Icon: CircleCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h4 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                  {title}
                </h4>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Kits au 93 + Kit T2 ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Kits prêts à l&apos;emploi
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Kits de cartons de déménagement au 93
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour simplifier votre préparation, nous proposons des kits de
            cartons spécialement conçus pour les déménagements
            {" "}{dept.nameWithArticle}. Chaque kit est adapté à un type de
            logement et regroupe l&apos;essentiel du matériel d&apos;emballage
            afin de vous permettre d&apos;organiser vos affaires plus
            facilement, sans avoir à chercher chaque élément séparément. Une
            solution pratique et complète.
          </p>
        </div>

        {/* Exemple Kit T2 */}
        <div className="mt-8 bg-[#EEF3F7] rounded-3xl border border-[#cfdbe6] p-6 md:p-8">
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center justify-center h-10 px-3 rounded-lg bg-[#237cb5] text-white text-[13px] font-extrabold tracking-wide">
              Exemple — Kit T2
            </span>
            <span className="text-[13.5px] text-[#5b6b7a] font-medium">
              Appartement 2 pièces — 35 à 50 m²
            </span>
          </div>
          <ul className="mt-5 space-y-3">
            {[
              "40 cartons standards : une belle quantité pour vider vos placards, vos étagères et vos armoires.",
              "20 cartons livres : de quoi emballer toute votre bibliothèque ou vos dossiers pro sans surcharger les cartons.",
              "2 rouleaux de papier bulle (1 m × 10 m) : pratique ! Un rouleau chacun pour emballer la vaisselle d'un côté et la déco de l'autre.",
              "4 rouleaux d'adhésif pro : de quoi fermer solidement et sécuriser le haut et le bas de chaque carton.",
            ].map((item, i) => (
              <li
                key={i}
                className="flex items-start gap-3 text-[14.5px] text-[#3a4856] leading-relaxed"
              >
                <CircleCheck
                  className="h-5 w-5 text-[#237cb5] mt-0.5 shrink-0"
                  strokeWidth={2.4}
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <Link
            to="/packs-cartons"
            className="mt-7 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="dept-93-cta-kits"
          >
            <Layers className="h-4 w-4" strokeWidth={2.6} />
            Découvrez nos autres kits (T1, T3 et le kit Maison)
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-white">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`dept-${dept.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Pourquoi choisir nos fournitures (5 atouts) ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Nos atouts
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Pourquoi choisir nos fournitures de déménagement&nbsp;?
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour garantir un déménagement bien organisé et sans mauvaise
            surprise, il est essentiel de s&apos;appuyer sur du matériel
            fiable et adapté. Nos fournitures sont conçues pour vous offrir
            protection, praticité et efficacité à chaque étape.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            {
              title: "Qualité professionnelle",
              body: "Des matériaux robustes pensés pour résister au transport et à la manutention.",
              Icon: ShieldCheck,
            },
            {
              title: "Protection optimale",
              body: "Emballages et accessoires adaptés pour sécuriser tous types de biens.",
              Icon: Package,
            },
            {
              title: "Utilisation simple",
              body: "Des produits pratiques qui facilitent la préparation de vos cartons.",
              Icon: CircleCheck,
            },
            {
              title: "Gamme complète",
              body: "Tout le nécessaire regroupé pour éviter les achats multiples.",
              Icon: Layers,
            },
            {
              title: "Fiabilité éprouvée",
              body: "Des fournitures utilisées par des professionnels du déménagement.",
              Icon: Truck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#FF7A00] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="dept-93-cta-shop"
          >
            Voir tous les cartons
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/emballage-protection"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="dept-93-cta-emballage"
          >
            <ShieldCheck className="h-4 w-4" strokeWidth={2.6} />
            Emballage et protection
          </Link>
        </div>
      </div>
    </section>
  </>
);

/* ============================================================
 * Template SEO — version "Val-de-Marne" (94) — contenu unique
 * ============================================================ */
const ValDeMarneSections = ({ dept }) => (
  <>
    {/* ======= SECTION 2 — Livraison locale Val-de-Marne ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison locale
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Vos cartons et fournitures de déménagement livrés chez vous
            {" "}{dept.nameWithArticle} ({dept.code})
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Vous résidez à <strong className="text-[#0f1d2b]">Créteil</strong>,
            {" "}<strong className="text-[#0f1d2b]">Vitry-sur-Seine</strong>,
            {" "}<strong className="text-[#0f1d2b]">Champigny-sur-Marne</strong>
            {" "}ou <strong className="text-[#0f1d2b]">Saint-Maur-des-Fossés</strong>&nbsp;?
            Gagnez du temps et simplifiez-vous la vie. Plus besoin de courir
            les magasins ou de charger votre voiture avec des paquets
            encombrants : nous vous apportons tout le matériel nécessaire
            directement à votre porte.
          </p>
          <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Nous assurons la livraison rapide de vos cartons, rouleaux de
            bulles et kits de protection sur l&apos;ensemble du 94 avec notre
            propre flotte de véhicules, garantissant un service ponctuel et
            des fournitures impeccables.
          </p>
          <Link
            to="/cartons-demenagement"
            className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="dept-94-cta-shop"
          >
            <Truck className="h-4 w-4" strokeWidth={2.6} />
            Commander mes cartons
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — 4 étapes simples emballage ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Méthode infaillible
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Quatre étapes simples pour réussir l&apos;emballage de vos cartons
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Préparer ses cartons ne s&apos;improvise pas. Pour gagner du temps
            au départ comme à l&apos;arrivée, découvrez la méthode infaillible
            pour emballer efficacement et en toute sécurité.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              n: "1",
              title: "Sécuriser le fond du carton",
              body:
                "Avant de remplir votre carton, renforcez le fond avec de l'adhésif de qualité professionnelle. Croisez les bandes en formant un « H » pour éviter que le carton ne cède sous le poids de vos affaires lors de la manutention.",
            },
            {
              n: "2",
              title: "Répartir les charges intelligemment",
              body:
                "Placez toujours les objets les plus lourds au fond (comme les livres ou la vaisselle) et complétez le dessus avec des éléments plus légers (linge, peluches). Veillez à ne pas dépasser 15 à 20 kg par carton pour pouvoir les soulever facilement.",
            },
            {
              n: "3",
              title: "Combler les espaces vides",
              body:
                "Un carton à moitié vide risque de s'écraser lorsqu'il sera empilé dans le camion. Utilisez du papier bulle, du papier journal ou même votre linge de maison pour caler les objets et empêcher qu'ils ne bougent pendant le transport.",
            },
            {
              n: "4",
              title: "Marquer et identifier chaque pièce",
              body:
                "À l'aide d'un marqueur, notez sur le dessus et sur au moins un côté le contenu du carton et sa pièce de destination (ex : Cuisine — Assiettes / Fragile). Cela vous évitera de chercher vos affaires pendant des jours une fois installé.",
            },
          ].map(({ n, title, body }) => (
            <div
              key={n}
              className="relative bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] p-5"
            >
              <div className="absolute top-4 right-4 text-[44px] font-extrabold text-[#237cb5]/15 leading-none">
                {n}
              </div>
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-9 w-9 rounded-full bg-[#237cb5] text-white text-[14px] font-extrabold">
                  {n}
                </span>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 bg-white border-l-4 border-[#FF7A00] rounded-r-2xl p-5 max-w-4xl shadow-sm">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF7A00]">
            L&apos;astuce en plus
          </div>
          <p className="mt-2 text-[14.5px] text-[#3a4856] leading-relaxed">
            Emballez d&apos;abord ce dont vous vous servez le moins (livres,
            vêtements hors saison, déco) et gardez pour la fin les objets du
            quotidien. Prévoyez aussi un <strong className="text-[#0f1d2b]">« carton de survie »</strong>
            {" "}pour le premier soir avec vos papiers importants, des
            chargeurs, une trousse de toilette et un peu de vaisselle&nbsp;!
          </p>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-white">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`dept-${dept.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Pourquoi nous faire confiance ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Notre engagement
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Pourquoi nous faire confiance&nbsp;?
          </h2>
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              title: "Livraison locale et rapide",
              body: `Une expédition programmée selon vos disponibilités partout ${dept.nameWithArticle}.`,
              Icon: Truck,
            },
            {
              title: "Qualité professionnelle",
              body: "Du matériel robuste et éprouvé pour sécuriser efficacement toutes vos affaires.",
              Icon: ShieldCheck,
            },
            {
              title: "Commandes soignées",
              body: "Une préparation rigoureuse pour vous garantir de recevoir exactement ce dont vous avez besoin.",
              Icon: Package,
            },
            {
              title: "Service client à votre écoute",
              body: "Des conseils experts pour vous aider à calibrer le nombre et le type de cartons nécessaires.",
              Icon: CircleCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#FF7A00] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="dept-94-cta-shop-bottom"
          >
            Voir tous les cartons
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/packs-cartons"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="dept-94-cta-kits-bottom"
          >
            <Layers className="h-4 w-4" strokeWidth={2.6} />
            Choisir un kit
          </Link>
        </div>
      </div>
    </section>
  </>
);

/* ============================================================
 * Template SEO — version "Val-d'Oise" (95) — contenu unique
 * ============================================================ */
const ValDOiseSections = ({ dept }) => (
  <>
    {/* ======= SECTION 2 — Livraison rapide partout 95 ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison locale
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Votre matériel de déménagement livré rapidement partout dans le
            {" "}{dept.code}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Plus besoin de courir les magasins ou de surcharger votre véhicule.
            Nous assurons la livraison rapide de vos cartons et fournitures
            directement à votre porte, que vous habitiez à Argenteuil, Cergy,
            Gonesse, Taverny ou Pontoise.
          </p>
          <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Grâce à notre propre service de transport, nous vous garantissons
            une livraison ponctuelle et des emballages acheminés en parfait
            état, selon vos disponibilités.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              title: "Gain de temps",
              body: "Commandez en ligne, nous nous occupons du reste.",
              Icon: Truck,
            },
            {
              title: "Zéro contrainte",
              body: "Des fournitures volumineuses livrées au pied de votre immeuble ou de votre maison.",
              Icon: ShieldCheck,
            },
            {
              title: "Service local",
              body: "Une logistique de proximité dédiée à 100 % aux habitants du Val-d'Oise.",
              Icon: MapPin,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Protégez et fermez vos cartons ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Protection &amp; fermeture
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Protégez et fermez vos cartons en toute sécurité
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour affronter les routes du Val-d&apos;Oise sans stress, de Cergy
            à Sarcelles ou d&apos;Argenteuil à Pontoise, de bons cartons ne
            suffisent pas. Sécurisez vos biens les plus précieux et scellez
            vos emballages efficacement grâce à nos accessoires de qualité
            professionnelle.
          </p>
        </div>

        {/* Sub-section: Protection */}
        <h3 className="mt-10 font-display font-extrabold text-lg md:text-xl text-[#0f1d2b]">
          Pour la protection de vos objets fragiles
        </h3>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              slug: "papier-bulle-1x100m",
              title: "Film bulle de protection",
              body: "L'allié indispensable pour envelopper la vaisselle, les écrans, les miroirs et les objets connectés. Il encaisse tous les chocs durant le transport dans le camion.",
              Icon: ShieldCheck,
            },
            {
              slug: "film-etirable",
              title: "Film étirable",
              body: "Parfait pour bloquer les portes de vos meubles, regrouper des éléments ou protéger vos canapés et matelas des salissures pendant la manutention.",
              Icon: Layers,
            },
          ].map(({ slug, title, body, Icon }) => (
            <Link
              key={slug}
              to={`/produit/${slug}`}
              className="group bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] p-5 hover:border-[#237cb5] hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-16px_rgba(35,124,181,0.35)] transition-all duration-300 block"
              data-testid={`dept-95-protection-${slug}`}
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white shrink-0">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h4 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b]">
                  {title}
                </h4>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
              <div className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-bold text-[#237cb5] opacity-0 group-hover:opacity-100 transition-opacity">
                Voir le produit <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.6} />
              </div>
            </Link>
          ))}
        </div>

        {/* Sub-section: Fermeture */}
        <h3 className="mt-10 font-display font-extrabold text-lg md:text-xl text-[#0f1d2b]">
          Pour la fermeture et le scellage
        </h3>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              slug: "rouleau-adhesif",
              title: "Adhésifs haute résistance",
              body: "Des rouleaux de scotch professionnels qui adhèrent parfaitement au carton et résistent aux variations de température, sans risquer de lâcher.",
              Icon: CircleCheck,
            },
            {
              slug: "devidoir-pistolet-adhesif",
              title: "Dévidoirs à scotch (pistolets)",
              body: "L'outil idéal pour fermer vos cartons à la chaîne, d'un seul geste et à un rythme efficace.",
              Icon: Package,
            },
          ].map(({ slug, title, body, Icon }) => (
            <Link
              key={slug}
              to={`/produit/${slug}`}
              className="group bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] p-5 hover:border-[#237cb5] hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-16px_rgba(35,124,181,0.35)] transition-all duration-300 block"
              data-testid={`dept-95-fermeture-${slug}`}
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white shrink-0">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h4 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b]">
                  {title}
                </h4>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
              <div className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-bold text-[#237cb5] opacity-0 group-hover:opacity-100 transition-opacity">
                Voir le produit <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.6} />
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-8 bg-[#0f1d2b] text-white rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-white/60">
              Livraison groupée 95
            </div>
            <p className="mt-1.5 text-[14.5px] leading-relaxed">
              Ajoutez vos accessoires et outils de fermeture directement à
              votre commande de cartons. Nous vous livrons tout en même temps
              chez vous dans le 95 pour vous éviter de vous retrouver bloqué
              en plein emballage&nbsp;!
            </p>
          </div>
          <Link
            to="/cartons-demenagement"
            className="shrink-0 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#FF7A00] hover:bg-[#E66E00] text-white font-bold text-[14px] transition-colors"
            data-testid="dept-95-cta-bundle"
          >
            Composer ma commande
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-white">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`dept-${dept.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Pourquoi nous faire confiance (4 atouts riches) ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Nos garanties
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Pourquoi nous faire confiance pour votre matériel de déménagement&nbsp;?
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Acheter ses fournitures chez nous, c&apos;est faire le choix de la
            sérénité, de la qualité professionnelle et d&apos;un service de
            proximité sans faille.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              title: "Matériel 100 % professionnel",
              body: "Nos cartons et protections ne sont pas des produits de grande surface. Ce sont les mêmes outils robustes et renforcés que nous utilisons au quotidien sur le terrain pour sécuriser les biens les plus précieux.",
              Icon: ShieldCheck,
            },
            {
              title: "Livraison en direct avec notre propre flotte",
              body: "Pas de sous-traitance ni de colis jetés à la va-vite. Nous gérons nous-mêmes vos livraisons partout en Île-de-France avec nos propres véhicules. C'est pour vous la garantie d'une ponctualité absolue et de fournitures livrées en parfait état.",
              Icon: Truck,
            },
            {
              title: "Une expertise reconnue",
              body: "Spécialistes de la logistique et du déménagement, nous connaissons vos besoins réels. Notre entreprise est couverte par une assurance solide (Allianz) pour vous garantir un professionnalisme et une fiabilité à chaque étape de votre commande.",
              Icon: CircleCheck,
            },
            {
              title: "Éco-responsabilité & proximité",
              body: "Nous privilégions des circuits logistiques optimisés au niveau local et des emballages de qualité supérieure, conçus pour résister et être réutilisés.",
              Icon: Recycle,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#FF7A00] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 bg-white border-l-4 border-[#237cb5] rounded-r-2xl p-5 max-w-4xl shadow-sm">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#237cb5]">
            Notre priorité
          </div>
          <p className="mt-2 text-[14.5px] text-[#3a4856] leading-relaxed">
            <strong className="text-[#0f1d2b]">Votre tranquillité.</strong> Des
            dizaines de particuliers et d&apos;entreprises comptent sur notre
            rigueur chaque année. En choisissant nos services, vous vous
            assurez un départ réussi et des affaires parfaitement protégées.
          </p>
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="dept-95-cta-shop-bottom"
          >
            Voir tous les cartons
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/packs-cartons"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="dept-95-cta-kits-bottom"
          >
            <Layers className="h-4 w-4" strokeWidth={2.6} />
            Choisir un kit
          </Link>
        </div>
      </div>
    </section>
  </>
);

export default DepartmentPage;
